import React, { useEffect, useMemo, useState } from 'react'

import { Checkbox, Popover, Space, Button } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'

export interface IColumnKeys {
  [key: string]: boolean
}

export interface IColumn {
  key: string
  name: string
}

export interface IColumnsSelectorProps {
  columns: IColumn[]
  visibleColumnKeys?: IColumnKeys
  defaultVisibleColumnKeys?: IColumnKeys
  onChange?: (visibleKeys: IColumnKeys) => void
}

const ColumnFilter: React.FC<
  React.PropsWithChildren<IColumnsSelectorProps>
> = ({
  columns,
  visibleColumnKeys,
  defaultVisibleColumnKeys,
  onChange,
  children
}) => {
  const [indeterminate, setIndeterminate] = useState(true)
  const [checkedAll, setCheckedAll] = useState(false)

  const filteredColumns = useMemo(
    () => columns.filter((c) => c.key !== 'dummy'),
    [columns]
  )

  const visibleKeys = useMemo(() => {
    if (visibleColumnKeys) {
      return visibleColumnKeys
    }
    return columns.reduce((acc, cur) => {
      acc[cur.key] = true
      return acc
    }, {} as IColumnKeys)
  }, [visibleColumnKeys, columns])

  useEffect(() => {
    function updateCheckAllStatus(columnKeys: IColumnKeys) {
      const checkedKeysCount = Object.keys(columnKeys).filter(
        (k) => columnKeys[k] && k !== 'dummy'
      ).length
      setIndeterminate(
        checkedKeysCount > 0 && checkedKeysCount < filteredColumns.length
      )
      setCheckedAll(checkedKeysCount === filteredColumns.length)
    }

    updateCheckAllStatus(visibleKeys)
  }, [visibleKeys, filteredColumns])

  function handleCheckAllChange(e: CheckboxChangeEvent) {
    const checked = e.target.checked
    const newVisibleKeys = columns.reduce((acc, cur) => {
      acc[cur.key] = checked
      return acc
    }, {} as IColumnKeys)
    onChange && onChange(newVisibleKeys)
  }

  function handleCheckChange(e: CheckboxChangeEvent, column: IColumn) {
    const checked = e.target.checked
    const newVisibleKeys = {
      ...visibleKeys,
      [column.key]: checked
    }
    onChange && onChange(newVisibleKeys)
  }

  const title = (
    <div>
      <Checkbox
        indeterminate={indeterminate}
        checked={checkedAll}
        onChange={handleCheckAllChange}
      >
        Select
      </Checkbox>
      {defaultVisibleColumnKeys && (
        <Button
          type='link'
          onClick={() => onChange && onChange(defaultVisibleColumnKeys)}
        >
          Reset
        </Button>
      )}
    </div>
  )

  const content = (
    <div style={{ marginTop: -12 }}>
      <Space
        direction='vertical'
        style={{
          maxHeight: 400,
          overflow: 'auto',
          paddingTop: 8,
          paddingBottom: 8
        }}
      >
        {filteredColumns.map((column) => (
          <Checkbox
            key={column.key}
            checked={visibleKeys[column.key]}
            onChange={(e) => handleCheckChange(e, column)}
          >
            {column.name}
          </Checkbox>
        ))}
      </Space>
    </div>
  )

  return (
    <Popover content={content} title={title} placement='bottomLeft'>
      {children}
    </Popover>
  )
}

export { ColumnFilter }
